import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useNotifications } from '../contexts/NotificationsContext';
import { navigateTo } from '../utils/navigation';
import { type NotificationItem } from '../api/notifications';

const timeAgo = (iso: string): string => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/** Notification center bell + dropdown. Reads live unread count from the
 * NotificationsProvider and lazily loads the feed when opened. */
const NotificationBell: React.FC = () => {
  const { unread, loadNotifications, markRead, refreshUnread } = useNotifications();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number; width: number; maxHeight: number }>({
    left: 0, top: 0, width: 340, maxHeight: 420,
  });
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    void loadNotifications(30, 0)
      .then(setItems)
      .finally(() => { setLoading(false); });
    void markRead().catch(() => { /* ignore mark-read failures */ });
  }, [open, loadNotifications, markRead]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      // The panel is portaled to <body>, so it's outside `ref`; keep it open
      // when the click lands inside either the bell or the panel.
      if (ref.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => { document.removeEventListener('mousedown', onDoc); };
  }, [open]);

  const onItem = (n: NotificationItem) => {
    setOpen(false);
    if (n.deep_link) navigateTo(n.deep_link);
    refreshUnread();
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        aria-label="Notifications"
        onClick={() => {
          if (!open && ref.current) {
            const r = ref.current.getBoundingClientRect();
            const width = Math.min(340, window.innerWidth - 24);
            // Anchor the panel's right edge to the bell, then clamp inside the
            // viewport so it never overflows (works for the top-left sidebar bell
            // and the top-right mobile-header bell alike).
            const left = Math.min(Math.max(12, r.right - width), window.innerWidth - width - 12);
            const top = r.bottom + 8;
            const maxHeight = Math.min(420, window.innerHeight - top - 12);
            setMenuPos({ left, top, width, maxHeight });
          }
          setOpen((v) => !v);
        }}
        style={{
          position: 'relative',
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: '1px solid var(--rule, rgba(31,36,33,.08))',
          background: 'var(--c-cream)',
          display: 'grid',
          placeItems: 'center',
          cursor: 'pointer',
          color: 'var(--ink)',
        }}
      >
        <span className="material-icons-round" style={{ fontSize: 22 }}>notifications</span>
        {unread > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              boxSizing: 'border-box',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 999,
              background: 'var(--c-coral, #F4A88E)',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              lineHeight: 1,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {ReactDOM.createPortal(
        <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            style={{
              position: 'fixed',
              left: menuPos.left,
              top: menuPos.top,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              overflowY: 'auto',
              background: 'var(--c-cream)',
              border: '1px solid var(--rule, rgba(31,36,33,.08))',
              borderRadius: 'var(--r-lg, 22px)',
              boxShadow: 'var(--sh-lifted)',
              zIndex: 60,
              padding: 'var(--s-3)',
            }}
          >
            <div style={{ padding: 'var(--s-2) var(--s-2) var(--s-3)' }}>
              <h3 className="t-h3" style={{ margin: 0, fontSize: 16 }}>Notifications</h3>
            </div>

            {loading ? (
              <p className="t-small t-mute" style={{ padding: 'var(--s-4)', textAlign: 'center' }}>Loading…</p>
            ) : items.length === 0 ? (
              <p className="t-small t-mute" style={{ padding: 'var(--s-5)', textAlign: 'center' }}>
                You&apos;re all caught up
              </p>
            ) : (
              <div className="stack-1">
                {items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { onItem(n); }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      background: n.read ? 'transparent' : 'var(--c-teal-weak, rgba(42,164,166,.10))',
                      border: 'none',
                      borderRadius: 'var(--r-md, 14px)',
                      padding: 'var(--s-3)',
                      cursor: n.deep_link ? 'pointer' : 'default',
                      display: 'flex',
                      gap: 'var(--s-3)',
                      alignItems: 'flex-start',
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="t-small" style={{ display: 'block', fontWeight: 600 }}>{n.title}</span>
                      <span className="t-label t-mute" style={{ display: 'block' }}>{n.body}</span>
                      <span className="t-label t-mute" style={{ display: 'block', marginTop: 2, opacity: 0.7 }}>
                        {timeAgo(n.created_at)}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default NotificationBell;
